/**
 * AI-harness primitives.
 *
 * The vocabulary the chat surface is built from: a status has one shape, a
 * tool call has one shape, a trace has one shape. Each takes its colour from
 * a `tone-*` class so the same status reads identically in the transcript, the
 * review gate and the pipeline panel — and follows any per-instance skin.
 *
 * Styles live in styles.css under "AI harness primitives".
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  IconCheck, IconChevronDown, IconCircle, IconCopy, IconSpinner, IconXCircle,
} from "../icons";

export type Tone = "neutral" | "accent" | "running" | "done" | "error" | "violet";
export type RunState = "pending" | "running" | "done" | "error";

const TONE_OF: Record<RunState, Tone> = {
  pending: "neutral", running: "running", done: "done", error: "error",
};

/** Mark for a run state — the same glyph everywhere a state is shown. */
export function StateMark({ state, size = 14 }: { state: RunState; size?: number }) {
  if (state === "running") return <IconSpinner size={size} />;
  if (state === "done") return <IconCheck size={size} />;
  if (state === "error") return <IconXCircle size={size} />;
  return <IconCircle size={size} />;
}

/** Status pill — professional-case label, colour-coded, optional live dot. */
export function Pill({
  tone = "neutral", dot, children,
}: {
  tone?: Tone;
  dot?: boolean;
  children: ReactNode;
}) {
  return (
    <span className={`pill tone-${tone}`}>
      {dot && <span className="pill-dot" aria-hidden="true" />}
      {children}
    </span>
  );
}

/** Text that carries the motion while something is in flight. */
export function Shimmer({ children }: { children: ReactNode }) {
  return <span className="shimmer">{children}</span>;
}

/** Seconds since `since`, ticking. */
function useElapsed(since: number): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(t);
  }, []);
  return `${((now - since) / 1000).toFixed(1)}s`;
}

/** Pixel-grid loader with an elapsed clock — the wait has a shape and a size. */
export function GridLoader({ label, since }: { label: ReactNode; since?: number }) {
  return (
    <span className="grid-loader" aria-live="polite">
      <span className="grid-loader-cells" aria-hidden="true">
        <i /><i /><i /><i /><i /><i /><i /><i /><i />
      </span>
      <Shimmer>{label}</Shimmer>
      {since !== undefined && <Elapsed since={since} />}
    </span>
  );
}

function Elapsed({ since }: { since: number }) {
  return <span className="elapsed">{useElapsed(since)}</span>;
}

export interface TraceStep {
  id: string;
  label: string;
  state: RunState;
}

/**
 * Thinking trace — one live line collapsed, the steps behind it expanded.
 * Opens itself while work is in flight and closes once the answer lands, so a
 * finished turn stays quiet.
 */
export function ThinkingTrace({
  label, steps, live, since, defaultOpen,
}: {
  label: ReactNode;
  steps: TraceStep[];
  live?: boolean;
  since?: number;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen ?? !!live);
  return (
    <div className={`trace${open ? " open" : ""}`}>
      <button
        className="trace-head"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        {live ? <GridLoader label={label} since={since} /> : <span>{label}</span>}
        {steps.length > 0 && <IconChevronDown size={14} className="chev" />}
      </button>
      {open && steps.length > 0 && (
        <div className="trace-steps">
          {steps.map(s => (
            <div key={s.id} className={`trace-step ${s.state}`}>
              <span className="trace-mark" aria-hidden="true">
                <StateMark state={s.state} />
              </span>
              <span className="trace-step-text">{s.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export interface ToolChipSpec {
  name: string;
  arg?: string;
  state: RunState;
}

/** Tool calls as chips — status, name, and the one argument worth showing. */
export function ToolChips({ tools }: { tools: ToolChipSpec[] }) {
  if (!tools.length) return null;
  return (
    <div className="tool-chips">
      {tools.map((t, i) => (
        <span key={`${t.name}-${i}`} className={`tool-chip ${t.state}`}>
          <span className="tool-mark" aria-hidden="true">
            <StateMark state={t.state} size={13} />
          </span>
          <span className="tool-name">{t.name}</span>
          {t.arg && <span className="tool-arg" title={t.arg}>{t.arg}</span>}
        </span>
      ))}
    </div>
  );
}

export interface TaskRowSpec {
  id: string;
  glyph?: string;
  label: string;
  detail?: string;
  state: RunState;
}

/** Task rows — a live run, one row per node, status on the right. */
export function TaskCard({
  title, rows, badge,
}: {
  title: ReactNode;
  rows: TaskRowSpec[];
  badge?: ReactNode;
}) {
  return (
    <div className="task-card">
      <div className="task-card-head">
        <span className="task-card-title">{title}</span>
        {badge}
      </div>
      <div className="task-rows">
        {rows.map(r => (
          <div key={r.id} className={`task-row ${r.state}`}>
            <span className={`task-glyph tone-${TONE_OF[r.state]}`} aria-hidden="true">
              {r.glyph ?? <StateMark state={r.state} size={13} />}
            </span>
            <span className="task-label">{r.label}</span>
            {r.detail && <span className="ctx-meta">{r.detail}</span>}
            <Pill tone={TONE_OF[r.state]} dot={r.state === "running"}>
              {STATE_LABEL[r.state]}
            </Pill>
          </div>
        ))}
      </div>
    </div>
  );
}

const STATE_LABEL: Record<RunState, string> = {
  pending: "Queued", running: "Running", done: "Done", error: "Failed",
};

/** Copy-to-clipboard button that reports back in place. */
export function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number>();
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return (
    <button
      className="msg-action"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          timer.current = window.setTimeout(() => setCopied(false), 1400);
        } catch { /* clipboard blocked — the button simply does nothing */ }
      }}
      title={label}
    >
      {copied ? <IconCheck size={13} /> : <IconCopy size={13} />}
      <span>{copied ? "Copied" : label}</span>
    </button>
  );
}

/** Code with its filename, language and a copy control. */
export function CodeCard({
  name, lang, code, children,
}: {
  name: string;
  lang?: string;
  code: string;
  children?: ReactNode;
}) {
  return (
    <div className="code-card">
      <div className="code-card-head">
        <span className="code-card-name">{name}</span>
        {lang && <Pill tone="neutral">{lang}</Pill>}
        <CopyButton text={code} />
      </div>
      {children ?? <pre className="code-body">{code}</pre>}
    </div>
  );
}

/** The human-in-the-loop gate: what will happen, then the decisions. */
export function ApprovalCard({
  icon, title, children, actions,
}: {
  icon?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  actions: ReactNode;
}) {
  return (
    <div className="approval">
      <div className="approval-head">
        {icon}
        <span className="approval-title">{title}</span>
      </div>
      {children && <div className="approval-body">{children}</div>}
      <div className="approval-actions">{actions}</div>
    </div>
  );
}

/** A retrieved chunk with the source it came from. */
export function ContextCard({
  title, meta, body, foot,
}: {
  title: ReactNode;
  meta?: ReactNode;
  body?: ReactNode;
  foot?: ReactNode;
}) {
  return (
    <div className="ctx-card">
      <div className="ctx-head">
        <span className="ctx-title">{title}</span>
        {meta && <span className="ctx-meta">{meta}</span>}
      </div>
      {body && <div className="ctx-body">{body}</div>}
      {foot && <div className="ctx-foot">{foot}</div>}
    </div>
  );
}
